
import React, { useState, useRef } from 'react';
import { SelectedConfig, ProductConfig, ComponentOption, ColorOption } from '../types';

interface VisualizerProps {
  config: ProductConfig;
  selection: SelectedConfig;
}

const Visualizer: React.FC<VisualizerProps> = ({ config, selection }) => {
  const [viewAngle, setViewAngle] = useState<'diagonal' | 'front'>('diagonal');
  const [isZoomed, setIsZoomed] = useState(false);
  const dragStartRef = useRef<number | null>(null);

  const activeColor = config.colors.find(c => c.id === selection.colorId) || config.colors[0];
  const activeHeadboard = config.headboards.find(h => h.id === selection.headboardId);
  const activeBase = config.bases.find(b => b.id === selection.baseId);
  const activeNightstand = config.nightstands.find(n => n.id === selection.nightstandId);
  const activeMattress = config.mattresses.find(m => m.id === selection.mattressId);
  const activeTopper = config.toppers.find(t => t.id === selection.topperId);
  const activeVoetbord = config.voetbords.find(v => v.id === selection.voetbordId);
  const activeVoet = config.voets.find(v => v.id === selection.voetId);
  const activePoot = config.poets.find(p => p.id === selection.pootId);

  const handleStart = (clientX: number) => {
    dragStartRef.current = clientX;
  };

  const handleMove = (clientX: number) => {
    if (dragStartRef.current === null) return;
    const diff = clientX - dragStartRef.current;
    if (Math.abs(diff) > 40) {
      setViewAngle(diff > 0 ? 'diagonal' : 'front');
      dragStartRef.current = clientX;
    }
  };

  const handleEnd = () => {
    dragStartRef.current = null;
  };

  const RenderLayer = ({ 
    component, 
    variantId,
    activeColor, 
    zIndex, 
    isFabric = false,
    label,
    customImgUrl,
    customScale = 1,
    yOffset = 0
  }: { 
    component?: ComponentOption, 
    variantId?: string,
    activeColor?: ColorOption, 
    zIndex: number,
    isFabric?: boolean,
    label: string,
    customImgUrl?: string,
    customScale?: number,
    yOffset?: number
  }) => {
    let imgUrl = customImgUrl || (viewAngle === 'front' ? component?.imageUrlFront : component?.imageUrl);
    
    if (!customImgUrl && variantId && component?.variants) {
      const variant = component.variants.find(v => v.id === variantId);
      if (variant) {
        const variantImg = viewAngle === 'front' ? variant.imageUrlFront : variant.imageUrl;
        if (variantImg) imgUrl = variantImg;
      }
    }

    if (!imgUrl) return null;

    return (
      <div 
        className="absolute inset-0 transition-all duration-500 ease-in-out select-none pointer-events-none flex items-center justify-center" 
        style={{ 
          zIndex,
          transform: `scale(${customScale}) translateY(${yOffset}%)`,
        }}
      >
        <div className="relative w-full h-full">
          <img 
            src={imgUrl} 
            alt={label} 
            className="w-full h-full object-contain"
            draggable={false}
          />
          
          {isFabric && activeColor && (
            <div 
              className="absolute inset-0 transition-all duration-700 pointer-events-none"
              style={{ 
                backgroundImage: activeColor.imageUrl ? `url(${activeColor.imageUrl})` : 'none',
                backgroundColor: activeColor.imageUrl ? 'transparent' : activeColor.hex,
                backgroundSize: '180px',
                backgroundRepeat: 'repeat',
                mixBlendMode: 'multiply',
                opacity: 0.65, 
                maskImage: `url(${imgUrl})`,
                WebkitMaskImage: `url(${imgUrl})`,
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskPosition: 'center'
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const isFirstMattress = activeMattress?.id === config.mattresses[0]?.id;
  const noneTopperImg = viewAngle === 'front' ? config.noneTopperImageUrlFront : config.noneTopperImageUrl;

  return (
    <div className="relative w-full aspect-[4/3] bg-white rounded-[2rem] overflow-hidden shadow-2xl border border-gray-100 group cursor-grab active:cursor-grabbing">
      <div 
        className="absolute inset-0 z-[100]"
        onMouseDown={(e) => handleStart(e.clientX)}
        onMouseMove={(e) => handleMove(e.clientX)}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={(e) => handleStart(e.touches[0].clientX)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
        onTouchEnd={handleEnd}
      />

      <div className="absolute top-6 left-1/2 -translate-x-1/2 z-[110] flex gap-2">
        <div className={`h-1 rounded-full transition-all duration-500 ${viewAngle === 'diagonal' ? 'bg-black w-6' : 'bg-gray-200 w-1.5'}`} />
        <div className={`h-1 rounded-full transition-all duration-500 ${viewAngle === 'front' ? 'bg-black w-6' : 'bg-gray-200 w-1.5'}`} />
      </div>

      <div className={`relative w-full h-full p-4 transition-transform duration-700 ease-out ${isZoomed ? 'scale-[1.8]' : 'scale-100'}`}>
        <RenderLayer component={activePoot} zIndex={5} label="Pootjes" />
        {selection.nightstandId !== 'n0' && <RenderLayer component={activeNightstand} activeColor={activeColor} zIndex={10} isFabric={true} label="Nachtkast" />}
        <RenderLayer component={activeHeadboard} variantId={selection.headboardVariantId} activeColor={activeColor} zIndex={20} isFabric={true} label="Hoofdbord" />
        <RenderLayer component={activeBase} activeColor={activeColor} zIndex={30} isFabric={true} label="Bodem" />
        <RenderLayer component={activeMattress} zIndex={40} label="Matras" activeColor={activeColor} isFabric={isFirstMattress} />
        {isFirstMattress && !selection.hasTopper && noneTopperImg && <RenderLayer customImgUrl={noneTopperImg} zIndex={45} label="Matras Finishing" isFabric={false} />}
        {selection.hasTopper && <RenderLayer component={activeTopper} zIndex={50} label="Topper" customScale={0.97} yOffset={-1.5} />}
        {selection.hasVoetbord && <RenderLayer component={activeVoetbord} activeColor={activeColor} zIndex={60} isFabric={true} label="Voetbord" />}
        {selection.hasVoet && <RenderLayer component={activeVoet} activeColor={activeColor} zIndex={70} isFabric={true} label="Puff" />}
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[110] opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => setIsZoomed(!isZoomed)} className="bg-black text-white px-5 py-2.5 rounded-full shadow-xl font-black text-[10px] uppercase tracking-widest transition-transform hover:scale-105 active:scale-95">
          {isZoomed ? 'Sluiten' : '🔍 Detail'}
        </button>
      </div>
    </div>
  );
};

export default Visualizer;
